import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Avatar from "../../ui/avatar/Avatar";
import LoadingSpinner from "../../ui/spinner/LoadingSpinner";
import SearchInput from "../../ui/inputs/SearchInput";
import { extractApiErrorMessage } from "../../utils/apiError";

import styles from "./LeadPersonPicker.module.css";

const SEARCH_DEBOUNCE_MS = 300;
const LOAD_MORE_THRESHOLD_PX = 60;

export const getPersonDisplayName = (person) =>
  person?.name ||
  [person?.first_name, person?.middle_name, person?.last_name]
    .filter(Boolean)
    .join(" ") ||
  person?.email ||
  "";

const matchesSearch = (person, searchTerms) => {
  if (!searchTerms.length) return true;

  const haystack = [
    getPersonDisplayName(person),
    person?.email || "",
    person?.position || "",
  ]
    .join(" ")
    .toLowerCase();

  return searchTerms.every((term) => haystack.includes(term));
};

const LISTBOX_NAVIGATION_KEYS = ["ArrowDown", "ArrowUp", "Home", "End"];

export const moveListboxFocus = (event) => {
  if (!LISTBOX_NAVIGATION_KEYS.includes(event.key)) return;

  const options = Array.from(
    event.currentTarget.querySelectorAll('[role="option"]:not(:disabled)'),
  );
  if (!options.length) return;

  const currentIndex = options.indexOf(document.activeElement);
  const lastIndex = options.length - 1;
  let nextIndex;

  if (event.key === "Home") nextIndex = 0;
  else if (event.key === "End") nextIndex = lastIndex;
  else if (event.key === "ArrowDown") nextIndex = Math.min(currentIndex + 1, lastIndex);
  else nextIndex = Math.max(currentIndex - 1, 0);

  event.preventDefault();
  options[nextIndex].focus();
};

export const usePersonPickerActions = ({ onAddPerson, onAdded }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const selectPerson = async (person) => {
    setSaving(true);
    setError(null);

    try {
      const addedPerson = await onAddPerson?.(person);
      onAdded?.(addedPerson);
    } catch (actionError) {
      setError(
        extractApiErrorMessage(actionError) ||
          "No se pudo agregar la persona al lead.",
      );
    } finally {
      setSaving(false);
    }
  };

  return { saving, error, clearError, selectPerson };
};

const LeadPersonPicker = ({
  candidates = [],
  loading = false,
  hasMore = false,
  onOpen,
  onSearch,
  onLoadMore,
  onSelect,
  onClose,
  saving = false,
  error = null,
  loadError = null,
  emptyMessage: emptyMessageOverride = null,
  fillContainer = false,
}) => {
  const [search, setSearch] = useState("");
  const searchTimerRef = useRef(null);
  const listRef = useRef(null);
  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;

  useEffect(() => {
    onOpenRef.current?.();

    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const visibleCandidates = useMemo(() => {
    if (onSearch) return candidates;

    const searchTerms = search
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    return candidates.filter((person) => matchesSearch(person, searchTerms));
  }, [candidates, onSearch, search]);

  const visibleCandidateCount = visibleCandidates.length;

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement || !onLoadMore || loading || loadError || !hasMore) return;

    const listHasNoScroll =
      listElement.clientHeight > 0 &&
      listElement.scrollHeight <= listElement.clientHeight;

    if (visibleCandidateCount === 0 || listHasNoScroll) onLoadMore();
  }, [visibleCandidateCount, loading, loadError, hasMore, onLoadMore]);

  const handleSearchChange = (value) => {
    const nextSearch = value ?? "";
    setSearch(nextSearch);

    if (!onSearch) return;

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(
      () => onSearch(nextSearch.trim()),
      SEARCH_DEBOUNCE_MS,
    );
  };

  const handleScroll = () => {
    const listElement = listRef.current;
    if (!listElement || !onLoadMore || loading || !hasMore) return;

    const distanceToBottom =
      listElement.scrollHeight - listElement.scrollTop - listElement.clientHeight;

    if (distanceToBottom < LOAD_MORE_THRESHOLD_PX) onLoadMore();
  };

  const emptyMessage =
    emptyMessageOverride ||
    (search.trim() ? "Sin resultados." : "No hay personas para agregar.");

  return (
    <div
      className={`${styles.picker} ${fillContainer ? styles.pickerFill : ""}`}
    >
      <SearchInput
        value={search}
        onChange={handleSearchChange}
        placeholder="Buscar persona..."
      />

      <div
        ref={listRef}
        className={styles.list}
        data-testid="person-picker-list"
        onScroll={handleScroll}
      >
        {visibleCandidates.length === 0 ? (
          loadError && !loading ? (
            <p className={styles.error} role="alert">
              {loadError}
            </p>
          ) : (
            <div className={styles.empty}>
              {loading ? "Cargando..." : emptyMessage}
            </div>
          )
        ) : (
          <>
            <div
              className={styles.options}
              role="listbox"
              aria-label="Personas"
              onKeyDown={moveListboxFocus}
            >
              {visibleCandidates.map((person) => {
                const displayName = getPersonDisplayName(person);
                const meta = [person.position, person.email || "Sin email"]
                  .filter(Boolean)
                  .join(" · ");

                return (
                  <button
                    key={person.id}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className={styles.option}
                    onClick={() => onSelect?.(person)}
                    disabled={saving}
                  >
                    <Avatar src={person.photo} name={displayName} size={24} />
                    <span className={styles.optionInfo}>
                      <span className={styles.optionName}>{displayName}</span>
                      <span className={styles.optionMeta}>{meta}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {loading && <div className={styles.empty}>Cargando...</div>}

            {loadError && !loading && (
              <p className={styles.error} role="alert">
                {loadError}
              </p>
            )}
          </>
        )}
      </div>

      {saving && (
        <div className={styles.saving} data-testid="picker-saving">
          <LoadingSpinner size={14} thickness={2} />
          <span>Agregando...</span>
        </div>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {onClose && (
        <div className={styles.footer}>
          <button
            type="button"
            className={styles.linkButtonMuted}
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  );
};

export default LeadPersonPicker;
